import React from "react";
import { Stack, TextField } from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import type { TargetFormValues, TeachingProcedure } from "../../TargetBuilderPanel.types";

const CaregiverFields: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const procedure = values.teaching_procedure;
  if (!procedure) return null;

  const update = (partial: Partial<TeachingProcedure>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  return (
    <Stack gap={5}>
      <FieldGroup label="Modeling">
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.coaching_modeling_notes ?? ""}
          onChange={(e) => update({ coaching_modeling_notes: e.target.value })}
          placeholder="Describe modeling steps for caregiver coaching"
          aria-label="Modeling"
        />
      </FieldGroup>

      <FieldGroup label="Rehearsal">
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.coaching_rehearsal_notes ?? ""}
          onChange={(e) => update({ coaching_rehearsal_notes: e.target.value })}
          placeholder="Describe rehearsal/practice plan"
          aria-label="Rehearsal"
        />
      </FieldGroup>

      <FieldGroup label="Feedback">
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.coaching_feedback_notes ?? ""}
          onChange={(e) => update({ coaching_feedback_notes: e.target.value })}
          placeholder="Describe caregiver feedback process"
          aria-label="Feedback"
        />
      </FieldGroup>

      <FieldGroup label="Visual Supports">
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.coaching_visual_supports_notes ?? ""}
          onChange={(e) => update({ coaching_visual_supports_notes: e.target.value })}
          placeholder="Document visual supports used in caregiver training"
          aria-label="Visual supports"
        />
      </FieldGroup>

      <FieldGroup label="Error Correction Procedure" required>
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.error_correction_procedure ?? ""}
          onChange={(e) => update({ error_correction_procedure: e.target.value })}
          placeholder="Describe caregiver-facing error correction steps"
          aria-label="Caregiver error correction procedure"
        />
      </FieldGroup>

      <FieldGroup label="Practice Opportunities per Session">
        <TextField
          size="small"
          type="number"
          value={procedure.practice_opportunities_per_session ?? ""}
          onChange={(e) => update({ practice_opportunities_per_session: e.target.value })}
          placeholder="e.g., 3"
          aria-label="Practice opportunities per session"
          inputProps={{ min: 0 }}
          InputProps={{ sx: { width: 120 } }}
        />
      </FieldGroup>
    </Stack>
  );
};

export default CaregiverFields;
